import React, { useState, useEffect } from 'react';
import StandardModal from '../../common/modals/StandardModal';
import { Table } from '../../common/components/Table';
import { Button } from '../../common/components/Button';
import { Checkbox } from '../../common/form-controls/Checkbox';
import '../../common/form-controls/Checkbox.css';
import SubscriptionRequestsService from '../../../services/SubscriptionRequestsService';
import { GrantConsentIdentity } from '../../../models/viewModels/Subscriptions';

interface GrantAdminConsentModalProps {
	visible: boolean;
	subscriptionId: number;
	onClose: () => void;
	showBanner: (x: any) => any;
	resource: any;
	setAlertBanner: any;
}

const GrantAdminConsentModal = ({ subscriptionId, visible, onClose, showBanner, resource, setAlertBanner }: GrantAdminConsentModalProps) => {
	const subscriptionRequestsService = new SubscriptionRequestsService();
	const [selectedPermissions, setSelectedPermissions] = useState([]);
	const [grantingAdminConsent, setGrantingAdminConsent] = useState<boolean>(false);
	const permissionsAvailable = [
		'Application.Read.All',
		'Directory.Read.All',
		'Group.Read.All',
		'User.Read.All',
	];

	const handlePermission = (permission) => {
		if (selectedPermissions.some((perm) => perm === permission)) {
			setSelectedPermissions(selectedPermissions.filter((perm) => perm !== permission));
			return;
		}
		setSelectedPermissions([...selectedPermissions, permission]);
	};

	const handleGrantConsent = async () => {
		setGrantingAdminConsent(true);
		const model: GrantConsentIdentity = {
			permissionsToAdd: selectedPermissions,
		};
		try{
			const res = await subscriptionRequestsService.grantAdminConsent(subscriptionId, resource.identity.principalId, model);
			setGrantingAdminConsent(false);
			onClose();
			setSelectedPermissions([]);
			showBanner(res.permissionsList);
		}
		catch(err){
			onClose();
			setGrantingAdminConsent(false);
			setSelectedPermissions([]);
			setAlertBanner({ 
                visible: true,
                text: 'An error occurred. Please try again later',
                variant: 'error'
            });
		}
	};

	useEffect(() => {
		setSelectedPermissions([]);
	}, [resource]);

	return (
		<StandardModal
			show={visible}
			onDismiss={grantingAdminConsent ? () => {} : onClose}
			blockOverlayClick={grantingAdminConsent}
			title={`Identity selected: '${resource?.name}'. Specify permissions to grant admin consent.`}
			footer={
				<Button
					style={{ float: 'right' }}
					disabled={selectedPermissions.length === 0 || grantingAdminConsent}
					variant="primary"
					onClick={handleGrantConsent}
				>
					{grantingAdminConsent ? 'Granting Admin Consent...' : 'Grant Admin Consent'}
				</Button>
			}
		>
			<Table>
				<Table.Head>
					<Table.Head.Row>
						<Table.Head.Cell>App/Permission</Table.Head.Cell>
						<Table.Head.Cell>Checked</Table.Head.Cell>
					</Table.Head.Row>
				</Table.Head>
				<Table.Body>
					{permissionsAvailable.map((permission) => (
						<Table.Row
							style={{ cursor: 'pointer' }}
							onClick={() => handlePermission(permission)}
							key={permission}
						>
							<Table.Cell>
								{permission}
							</Table.Cell>
							<div onClick={(e) => e.stopPropagation()}>
								<Checkbox
									className="chk-no-borders"
									value={permission}
									id={permission}
									checked={selectedPermissions.some((perm) => perm === permission)}
									onChange={() => handlePermission(permission)}
								/>
							</div>
						</Table.Row>
					))}
				</Table.Body>
			</Table>
		</StandardModal>
	);
};

export default GrantAdminConsentModal;
